<template>
  <OriginalMedia v-bind="props">
    <template #video="{ video }">
      <video
        :ref="(e) => videos.push(e as HTMLVideoElement)"
        controls
        :src="video.video?.filename ?? undefined"
        :poster="video.thumbnail?.filename ?? undefined"
        :title="video.video?.title ?? undefined"
        @play="handleVideoPlayInteraction($event.target as HTMLVideoElement)"
        @pause="handleVideoPauseInteraction($event.target as HTMLVideoElement)"
        @ended="handleVideoEndedInteraction($event.target as HTMLVideoElement)"
      >
        Sorry, your browser does not support embedded videos.
      </video>
    </template>
  </OriginalMedia>
</template>
<script lang="ts" setup>
import { type AspectRatio, WPElementMedia as OriginalMedia, type WPElementMediaBlok } from '@webcarelbj/shared-bloks'

const { trackView, trackVideo } = useTracking()

const props = defineProps<{
  blok: WPElementMediaBlok
  aspectRatio: AspectRatio | string
}>()

const videos = ref<HTMLVideoElement[]>([])

const videoInteractions: VideoInteraction[] = []

const trackedVideoViews: number[] = []
let intervalTimer: ReturnType<typeof setInterval> | undefined
const isInViewportThreshold = window?.video_view_timeout ?? 2000 
const isInViewportTimer: Record<number, ReturnType<typeof setTimeout>> = {}
// Expected scenario, if the IntersectionObserver doesn't exist, we just don't track views, but we can still
// track interactions.
let observer: IntersectionObserver | null = null
const createObserver = () => {
  return new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const video = entry.target as HTMLVideoElement
      const index = videos.value.indexOf(video)
      if (!trackedVideoViews.includes(index)) {
        if (entry.isIntersecting) {
          isInViewportTimer[index] = setTimeout(() => {
            trackView({
              view_type: 'view_video',
              label: video.title,
            })
            trackedVideoViews.push(index)
          }, isInViewportThreshold)
        } else {
          clearTimeout(isInViewportTimer[index])
        }
      }
    })
  }, {
    root: null, // viewport = root-element
    rootMargin: '0px',
    threshold: 0.5, // 50% must be visible
  })
}

onMounted(() => {
  for (const video of videos.value) {
    videoInteractions.push({
      video_percent: '0%',
      video_progress: '',
      video_title: video.title,
    })
  }

  if ('IntersectionObserver' in window) {
    observer = createObserver()
    for (const video of videos.value) {
      observer.observe(video)
    }
  }
})

onUnmounted(() => {
  if (observer) {
    for (const video of videos.value) {
      observer.unobserve(video)
    }
  }
})

const updateVideoInteraction = (video: HTMLVideoElement, interactionUpdate: Partial<VideoInteraction>) => {
  const index = videos.value.indexOf(video)
  const interaction = {
    ...videoInteractions[index],
    ...interactionUpdate,
  }
  videoInteractions[index] = interaction

  if (interaction.video_percent !== '100%' && interaction.video_progress !== 'end') {
    trackVideo(interaction)
  } else if (interaction.video_progress === 'end') {
    trackVideo(interaction)
  }
}

const handleVideoPlayInteraction = (video: HTMLVideoElement) => {
  const delay = (video.duration - video.currentTime) / 10

  intervalTimer = setInterval(() => {
    updateVideoInteraction(video, {
      video_percent: Math.round(video.currentTime / video.duration) * 100 + '%',
    })
  }, delay * 1000)

  updateVideoInteraction(video, {
    video_progress: 'play',
  })
}

const handleVideoPauseInteraction = (video: HTMLVideoElement) => {
  updateVideoInteraction(video, {
    video_progress: 'pause',
  })
  clearInterval(intervalTimer)
}

const handleVideoEndedInteraction = (video: HTMLVideoElement) => {
  updateVideoInteraction(video, {
    video_progress: 'end',
    video_percent: '100%',
  })
  clearInterval(intervalTimer)
}
</script>
<style scoped lang="scss">
:is(video) {
  width: 100%;
  height: auto;
  object-fit: cover;
}
</style>
